import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Plus, Crown, Clock, Check, Loader2, CalendarDays, Pencil, Trash2, ShieldCheck, ArrowRight, Instagram, Facebook, Globe, TrendingUp, Copy, Sparkles } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import Sparkline from "../components/Sparkline";
import MultiSparkline from "../components/MultiSparkline";
import { useAppConfig } from "../lib/config";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const { payments_enabled } = useAppConfig();
  const [venues, setVenues] = useState([]);
  const [events, setEvents] = useState([]);
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [packages, setPackages] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [myTours, setMyTours] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/my/venues").then((r) => setVenues(r.data));
    api.get("/my/events").then((r) => setEvents(r.data));
    // Only fetch sponsorship packages when monetisation is on.
    if (payments_enabled) {
      api.get("/payments/packages").then((r) => setPackages(r.data)).catch(() => {});
    }
    api.get("/my/venues/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    api.get("/my/tours").then((r) => setMyTours(r.data)).catch(() => {});
  }, [user, payments_enabled]);

  const startSponsor = async (venueId, packageId) => {
    setBusyId(`${venueId}-${packageId}`);
    try {
      const r = await api.post("/payments/checkout", {
        package_id: packageId,
        venue_id: venueId,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally {
      setBusyId(null);
    }
  };

  const deleteVenue = async (id) => {
    try {
      await api.delete(`/venues/${id}`);
      setVenues((s) => s.filter((v) => v.id !== id));
      toast.success("Venue deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const deleteEvent = async (id) => {
    try {
      await api.delete(`/events/${id}`);
      setEvents((s) => s.filter((e) => e.id !== id));
      toast.success("Event deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  if (!user) return null;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Admin shortcut banner */}
        {user.role === "admin" && (
          <Link
            to="/admin"
            className="card-hover flex items-center gap-4 bg-navy text-white rounded-2xl p-5 mb-8 group"
            data-testid="admin-banner"
          >
            <div className="bg-sponsored text-navy rounded-xl p-3 shrink-0">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="font-heading text-lg">Admin Control Room</div>
              <div className="text-sm text-white/70">Approve submissions, manage editor's picks, publish stories.</div>
            </div>
            <ArrowRight className="w-5 h-5 text-white/70 group-hover:text-white group-hover:translate-x-1 transition-all" />
          </Link>
        )}

        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <span className="text-sm tracking-widest text-coastal uppercase">Dashboard</span>
            <h1 className="font-heading text-4xl lg:text-5xl text-navy tracking-tight mt-2" data-testid="dashboard-title">
              Hi {user.name.split(" ")[0]},
            </h1>
            <p className="text-neutral-500 mt-2">Manage your listings, events & sponsorships.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/dashboard/submit">
              <Button className="rounded-full bg-navy hover:bg-navy/90" data-testid="submit-venue-btn">
                <Plus className="w-4 h-4 mr-2" /> Submit a venue
              </Button>
            </Link>
            <Link to="/dashboard/submit-event">
              <Button variant="outline" className="rounded-full border-navy/30 text-navy hover:bg-navy hover:text-white" data-testid="submit-event-btn">
                <CalendarDays className="w-4 h-4 mr-2" /> Submit an event
              </Button>
            </Link>
            <Link to="/dashboard/submit?free=1">
              <Button variant="outline" className="rounded-full border-emerald-500/40 text-emerald-700 hover:bg-emerald-600 hover:text-white" data-testid="submit-activity-btn">
                <Sparkles className="w-4 h-4 mr-2" /> Submit an Activity
              </Button>
            </Link>
            <Link to="/dashboard/submit-tour">
              <Button variant="outline" className="rounded-full border-coastal/40 text-coastal hover:bg-coastal hover:text-white" data-testid="submit-tour-btn">
                <Plus className="w-4 h-4 mr-2" /> Submit a tour
              </Button>
            </Link>
          </div>
        </div>

        <h2 className="font-heading text-2xl text-navy mb-4">Your venues</h2>
        {venues.length === 0 ? (
          <div className="bg-white border border-black/5 rounded-2xl p-10 text-center">
            <p className="text-neutral-500 mb-4">You haven't submitted any venues yet.</p>
            <Link to="/dashboard/submit">
              <Button className="rounded-full bg-navy hover:bg-navy/90">Submit your first venue</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {venues.map((v) => (
              <div key={v.id} className="bg-white border border-black/5 rounded-2xl p-6 flex flex-col lg:flex-row gap-6" data-testid={`dashboard-venue-${v.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-heading text-xl text-navy">{v.name}</h3>
                    {v.is_sponsored && (
                      <span className="sponsored-ribbon text-xs px-2.5 py-0.5 rounded-full flex items-center gap-1">
                        <Crown className="w-3 h-3" /> Sponsored
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-neutral-500 flex items-center gap-3 flex-wrap">
                    <span className="capitalize">{v.category}</span>
                    <span>·</span>
                    <span>{v.location}</span>
                    <span>·</span>
                    <span className={`flex items-center gap-1 ${v.status === "approved" ? "text-emerald-600" : v.status === "pending" ? "text-sponsored" : "text-rose-500"}`}>
                      {v.status === "approved" ? <Check className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                      {v.status}
                    </span>
                  </div>
                  {v.is_sponsored && v.sponsored_until && (
                    <p className="text-xs text-neutral-500 mt-2">Sponsored until {new Date(v.sponsored_until).toLocaleDateString()}</p>
                  )}
                  {analytics[v.id] && (analytics[v.id].instagram_total + analytics[v.id].facebook_total + analytics[v.id].website_total > 0) && (
                    <div className="mt-3 space-y-2" data-testid={`analytics-${v.id}`}>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-neutral-500">
                        <span className="text-[10px] uppercase tracking-widest text-coastal font-medium">Last 7 days</span>
                        {analytics[v.id].instagram_total > 0 && (
                          <span className="inline-flex items-center gap-1.5">
                            <Instagram className="w-3.5 h-3.5 text-coastal" />
                            <strong className="text-navy">{analytics[v.id].instagram_7d}</strong>
                            <span className="text-neutral-400">/ {analytics[v.id].instagram_total} all-time</span>
                          </span>
                        )}
                        {analytics[v.id].facebook_total > 0 && (
                          <span className="inline-flex items-center gap-1.5">
                            <Facebook className="w-3.5 h-3.5 text-coastal" />
                            <strong className="text-navy">{analytics[v.id].facebook_7d}</strong>
                            <span className="text-neutral-400">/ {analytics[v.id].facebook_total} all-time</span>
                          </span>
                        )}
                        {analytics[v.id].website_total > 0 && (
                          <span className="inline-flex items-center gap-1.5">
                            <Globe className="w-3.5 h-3.5 text-coastal" />
                            <strong className="text-navy">{analytics[v.id].website_7d}</strong>
                            <span className="text-neutral-400">/ {analytics[v.id].website_total} all-time</span>
                          </span>
                        )}
                      </div>
                      {analytics[v.id].daily_30d && analytics[v.id].daily_30d.some((n) => n > 0) && (
                        <div className="flex items-start gap-3" data-testid={`sparkline-${v.id}`}>
                          <TrendingUp className="w-3.5 h-3.5 text-coastal/70 shrink-0 mt-1" />
                          {analytics[v.id].daily_30d_by_channel ? (
                            <MultiSparkline byChannel={analytics[v.id].daily_30d_by_channel} width={200} height={42} />
                          ) : (
                            <Sparkline data={analytics[v.id].daily_30d} width={160} height={32} />
                          )}
                          <span className="text-[10px] uppercase tracking-widest text-neutral-400 mt-1">30-day · hover</span>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => navigate(`/dashboard/edit-venue/${v.id}`)}
                      className="text-xs flex items-center gap-1 px-3 py-1 rounded-full border border-navy/20 text-navy hover:bg-navy hover:text-white transition-colors"
                      data-testid={`edit-venue-${v.id}`}
                    >
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                    {v.status === "pending" && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button
                            className="text-xs flex items-center gap-1 px-3 py-1 rounded-full border border-rose-200 text-rose-600 hover:bg-rose-50 transition-colors"
                            data-testid={`delete-venue-${v.id}`}
                          >
                            <Trash2 className="w-3 h-3" /> Delete
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this submission?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Permanently removes <strong>{v.name}</strong>. This action can't be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteVenue(v.id)} className="bg-rose-500 hover:bg-rose-600" data-testid={`confirm-delete-venue-${v.id}`}>
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-2 min-w-[280px]">
                  {payments_enabled ? (
                    <>
                      <p className="text-xs text-neutral-500 uppercase tracking-wider">Boost with sponsorship</p>
                      <div className="grid grid-cols-3 gap-2">
                        {packages.map((p) => (
                          <button
                            key={p.id}
                            disabled={busyId === `${v.id}-${p.id}` || v.status !== "approved"}
                            onClick={() => startSponsor(v.id, p.id)}
                            className="px-3 py-2 rounded-lg border border-navy/20 text-navy hover:bg-navy hover:text-white transition-colors disabled:opacity-50 text-center"
                            data-testid={`sponsor-${v.id}-${p.id}`}
                          >
                            {busyId === `${v.id}-${p.id}` ? (
                              <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                            ) : (
                              <>
                                <div className="text-xs font-semibold capitalize">{p.id}</div>
                                <div className="text-[11px] opacity-70">${p.amount}</div>
                              </>
                            )}
                          </button>
                        ))}
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* My events */}
        <div className="flex items-center justify-between mt-12 mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-heading text-2xl text-navy">Your events</h2>
            {events.length > 0 && (() => {
              const now = new Date();
              const upcoming = events.filter((e) => new Date(e.start_time) >= now).length;
              const past = events.length - upcoming;
              return (
                <div className="inline-flex rounded-full border border-black/10 p-0.5 text-xs" data-testid="event-time-filter">
                  <button
                    type="button"
                    onClick={() => setShowPastEvents(false)}
                    className={`px-3 py-1 rounded-full transition-colors ${!showPastEvents ? "bg-navy text-white" : "text-neutral-600 hover:text-navy"}`}
                    data-testid="filter-upcoming"
                  >
                    Upcoming <span className="opacity-70">({upcoming})</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPastEvents(true)}
                    className={`px-3 py-1 rounded-full transition-colors ${showPastEvents ? "bg-navy text-white" : "text-neutral-600 hover:text-navy"}`}
                    data-testid="filter-past"
                  >
                    Past <span className="opacity-70">({past})</span>
                  </button>
                </div>
              );
            })()}
          </div>
          <div className="flex gap-2">
            <Link to="/dashboard/quick-gig">
              <Button className="rounded-full bg-coastal hover:bg-coastal/90 text-white" data-testid="quick-add-gig-btn">
                <Sparkles className="w-4 h-4 mr-1.5 text-sponsored" /> Quick add gig
              </Button>
            </Link>
            <Link to="/dashboard/submit-event">
              <Button variant="outline" className="rounded-full border-navy/30 text-navy hover:bg-navy hover:text-white" data-testid="submit-event-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Submit event
              </Button>
            </Link>
          </div>
        </div>
        {(() => {
          const now = new Date();
          const filtered = events
            .filter((e) => showPastEvents ? new Date(e.start_time) < now : new Date(e.start_time) >= now)
            .sort((a, b) => showPastEvents
              ? new Date(b.start_time) - new Date(a.start_time)  // past: newest first
              : new Date(a.start_time) - new Date(b.start_time)); // upcoming: soonest first
          if (events.length === 0) {
            return (
              <div className="bg-white border border-black/5 rounded-2xl p-10 text-center">
                <p className="text-neutral-500 mb-4">You haven&apos;t submitted any events yet.</p>
                <Link to="/dashboard/submit-event">
                  <Button variant="outline" className="rounded-full border-navy/30 text-navy hover:bg-navy hover:text-white">
                    Submit your first event
                  </Button>
                </Link>
              </div>
            );
          }
          if (filtered.length === 0) {
            return (
              <div className="bg-white border border-black/5 rounded-2xl p-8 text-center text-neutral-500" data-testid="events-empty-filter">
                {showPastEvents ? "No past events yet." : "No upcoming events. Submit a new one or check past events."}
              </div>
            );
          }
          return (
          <div className="grid md:grid-cols-2 gap-4">
            {filtered.map((e) => (
              <div key={e.id} className="bg-white border border-black/5 rounded-2xl p-5" data-testid={`dashboard-event-${e.id}`}>
                <div className="flex items-start justify-between gap-3 mb-1">
                  <h3 className="font-heading text-lg text-navy">{e.title}</h3>
                  <span className={`text-xs px-2.5 py-0.5 rounded-full capitalize flex items-center gap-1 shrink-0 ${e.status === "approved" ? "bg-emerald-50 text-emerald-700" : e.status === "pending" ? "bg-sponsored/20 text-navy" : "bg-rose-50 text-rose-600"}`}>
                    {e.status === "approved" ? <Check className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                    {e.status}
                  </span>
                </div>
                <div className="text-sm text-neutral-500 flex items-center gap-2 flex-wrap">
                  <CalendarDays className="w-3.5 h-3.5 text-coastal" />
                  {e.is_recurring && <span className="bg-coastal/10 text-coastal text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-medium">{e.recurrence}</span>}
                  {new Date(e.start_time).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  {e.location && <><span>·</span><span>{e.location}</span></>}
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => navigate(`/dashboard/edit-event/${e.id}`)}
                    className="text-xs flex items-center gap-1 px-3 py-1 rounded-full border border-navy/20 text-navy hover:bg-navy hover:text-white transition-colors"
                    data-testid={`edit-event-${e.id}`}
                  >
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  <button
                    onClick={() => {
                      const dup = {
                        title: e.title,
                        description: e.description,
                        venue_id: e.venue_id,
                        venue_name: e.venue_name,
                        location: e.location,
                        start_time: null,
                        end_time: null,
                        category: e.category,
                        artist: e.artist,
                        tags: e.tags || [],
                        image: e.image,
                        price: e.price,
                        booking_url: e.booking_url,
                        is_recurring: e.is_recurring,
                        recurrence: e.recurrence,
                      };
                      const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(dup))));
                      navigate(`/dashboard/submit-event?duplicate=${encoded}`);
                    }}
                    className="text-xs flex items-center gap-1 px-3 py-1 rounded-full border border-coastal/30 text-coastal hover:bg-coastal hover:text-white transition-colors"
                    data-testid={`duplicate-event-${e.id}`}
                  >
                    <Copy className="w-3 h-3" /> Duplicate
                  </button>
                  {(e.status === "pending" || (e.status === "approved" && new Date(e.start_time) < new Date())) && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          className="text-xs flex items-center gap-1 px-3 py-1 rounded-full border border-rose-200 text-rose-600 hover:bg-rose-50 transition-colors"
                          data-testid={`delete-event-${e.id}`}
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this event?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Permanently removes <strong>{e.title}</strong>. This action can&apos;t be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteEvent(e.id)} className="bg-rose-500 hover:bg-rose-600" data-testid={`confirm-delete-event-${e.id}`}>
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            ))}
          </div>
          );
        })()}

        {/* My tours — visible only if the user owns at least one activity operator */}
        {venues.some((v) => v.category === "activity") && (
          <div data-testid="my-tours">
            <div className="flex items-end justify-between mt-12 mb-4">
              <div>
                <h2 className="font-heading text-2xl text-navy">Your tours</h2>
                <p className="text-sm text-neutral-500 mt-1">Experiences visitors see under <Link to="/directory/activity" className="text-coastal underline">Play</Link>.</p>
              </div>
              <Link to="/dashboard/submit-tour">
                <Button variant="outline" className="rounded-full border-coastal/40 text-coastal hover:bg-coastal hover:text-white" data-testid="submit-tour-btn-2">
                  <Plus className="w-4 h-4 mr-1.5" /> Add a tour
                </Button>
              </Link>
            </div>
            {myTours.length === 0 ? (
              <div className="bg-white border border-black/5 rounded-2xl p-10 text-center" data-testid="my-tours-empty">
                <p className="text-neutral-500 mb-4">No tours yet. Add your first experience.</p>
                <Link to="/dashboard/submit-tour">
                  <Button className="rounded-full bg-navy hover:bg-navy/90">Add your first tour</Button>
                </Link>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {myTours.map((t) => (
                  <div key={t.id} className="bg-white border border-black/5 rounded-2xl p-5 flex items-start gap-4" data-testid={`tour-row-${t.id}`}>
                    {t.image && <img src={t.image} alt="" className="w-20 h-20 object-cover rounded-xl shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <Link to={`/tour/${t.id}`} className="font-heading text-lg text-navy hover:text-coastal truncate block">{t.title}</Link>
                      <div className="text-xs text-neutral-500 mt-0.5 capitalize">{t.activity_type.replace(/_/g, " ")} · From ${Math.round(t.price_from)} {t.currency}</div>
                      <div className="text-xs text-neutral-400 mt-1">{t.duration_label}</div>
                    </div>
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Delete "${t.title}"? This can't be undone.`)) return;
                        try {
                          await api.delete(`/tours/${t.id}`);
                          setMyTours((arr) => arr.filter((x) => x.id !== t.id));
                          toast.success("Tour deleted.");
                        } catch {
                          toast.error("Could not delete tour");
                        }
                      }}
                      className="text-xs text-rose-500 hover:text-rose-700 inline-flex items-center gap-1 shrink-0"
                      data-testid={`delete-tour-${t.id}`}
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                    <Link
                      to={`/dashboard/edit-tour/${t.id}`}
                      className="text-xs text-navy hover:text-coastal inline-flex items-center gap-1 shrink-0 ml-3"
                      data-testid={`edit-tour-${t.id}`}
                    >
                      Edit
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
